import { motion } from "framer-motion";
import { ShieldX, Wrench, Store } from "lucide-react";
import { FadeUp, MaskedLines, Chapter } from "@/components/motion";
import { CTAButton } from "@/components/CTA";
import Countdown24 from "@/components/Countdown24";

const INCLUDES = [
  { num: "01", title: "RECEPCIÓN Y ANÁLISIS", desc: "Recibimos tu incidencia —descripción y vídeo— y la analizamos con la mayor brevedad posible." },
  { num: "02", title: "ASIGNACIÓN DEL ESPECIALISTA", desc: "Comunicamos la incidencia al técnico especializado correspondiente a cada tipo de avería." },
  { num: "03", title: "COORDINACIÓN COMPLETA", desc: "Gestionamos la relación con el profesional para que tú no tengas que perseguir respuestas." },
  { num: "04", title: "COMPROMISO 24H HÁBILES", desc: "Desde la gestión, cuenta atrás para disponer de un técnico en tu comercio en menos de 24 horas hábiles." },
];

const NOT = [
  { icon: ShieldX, title: "NO ES UN SEGURO", desc: "Aprea no es una aseguradora. Es una gestora de incidencias: gestión activa, no pólizas." },
  { icon: Wrench, title: "NO ES UNA EMPRESA DE REPARACIONES", desc: "No reparamos nosotros: gestionamos que el técnico especializado adecuado llegue a tu comercio." },
  { icon: Store, title: "NO ES UN MARKETPLACE", desc: "No buscas ni comparas técnicos. Aprea es tu único punto de contacto para cualquier incidencia." },
];

export default function Servicio() {
  return (
    <main data-testid="page-servicio" className="pt-40 lg:pt-48">
      <header className="mx-auto max-w-[1500px] px-6 lg:px-12 pb-24 lg:pb-32">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="font-mono text-[11px] tracking-[0.4em] text-glow/80 mb-8"
        >
          GESTIÓN, NO REPARACIÓN
        </motion.p>
        <h1 className="font-display font-800 uppercase leading-[0.95] tracking-tight text-[12vw] sm:text-7xl lg:text-8xl text-offwhite">
          <MaskedLines
            mode="load"
            delay={0.3}
            lines={[
              <span key="a">El</span>,
              <span key="b" className="font-cormorant normal-case font-medium text-metal">servicio.</span>,
            ]}
          />
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.8 }}
          className="mt-9 max-w-xl text-mist text-base md:text-lg leading-relaxed"
        >
          Mediante una cuota mensual, tu comercio tiene un gestor permanente de incidencias físicas. Cuando algo
          falla, notificas y Aprea se encarga del resto.
        </motion.p>
      </header>

      <section className="border-t border-silver/10 py-24 lg:py-32">
        <div className="mx-auto max-w-[1500px] px-6 lg:px-12">
          <Chapter num="01" label="QUÉ INCLUYE LA GESTIÓN" className="mb-14" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {INCLUDES.map((s, i) => (
              <FadeUp key={s.num} delay={i * 0.06}>
                <div className="group border border-silver/10 bg-panel/40 p-9 h-full hover:border-silver/30 transition-colors duration-500">
                  <span className="font-mono text-[10px] tracking-[0.3em] text-steel block mb-6">{s.num}</span>
                  <h2 className="font-oswald font-600 uppercase text-2xl tracking-wide text-offwhite mb-4">
                    {s.title}
                  </h2>
                  <p className="text-mist text-sm md:text-base leading-relaxed">{s.desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-abyss border-y border-silver/10 py-24 lg:py-32">
        <div className="mx-auto max-w-[1500px] px-6 lg:px-12">
          <Chapter num="02" label="PARA QUE NO QUEPA DUDA" className="mb-14" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {NOT.map((n, i) => (
              <FadeUp key={n.title} delay={i * 0.08}>
                <div className="border border-silver/10 p-9 h-full">
                  <n.icon className="h-6 w-6 text-glow/80 mb-8" strokeWidth={1.5} />
                  <h2 className="font-oswald font-600 uppercase text-xl tracking-wide text-offwhite mb-4">{n.title}</h2>
                  <p className="text-mist text-sm leading-relaxed">{n.desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-[1500px] px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-6">
            <Chapter num="03" label="LA CUOTA" className="mb-10" />
            <FadeUp>
              <h2 className="font-display font-800 uppercase text-4xl sm:text-5xl tracking-tight text-offwhite leading-tight mb-8">
                Una cuota mensual. <span className="font-cormorant normal-case font-medium text-metal">Cero gestiones.</span>
              </h2>
            </FadeUp>
            <FadeUp delay={0.1}>
              <p className="text-mist leading-relaxed max-w-md mb-8">
                El servicio funciona mediante suscripción mensual. El alta online estará disponible próximamente
                con toda la información de planes y condiciones.
              </p>
              <div className="space-y-3 font-mono text-xs text-steel">
                <p data-testid="servicio-precio">CUOTA — 12,99 € / MES</p>
                <p>PAGO — CARGO RECURRENTE CON TARJETA, PLATAFORMA SEGURA</p>
              </div>
            </FadeUp>
            <FadeUp delay={0.2} className="mt-10">
              <CTAButton testid="servicio-cta-alta">DARME DE ALTA</CTAButton>
            </FadeUp>
          </div>
          <FadeUp delay={0.15} className="lg:col-span-6">
            <div className="border border-silver/15 bg-panel/40 p-9">
              <span className="font-mono text-[10px] tracking-[0.35em] text-glow/80 block mb-8">
                EL COMPROMISO, EN DIRECTO
              </span>
              <Countdown24 testid="servicio-countdown" className="[&>div:first-child]:!text-[13vw] md:[&>div:first-child]:!text-7xl" />
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
